import { Stack } from '@mui/material'
import type { GridColDef, GridRowParams } from '@mui/x-data-grid'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import FlagIcon from '@/components/dataDisplay/FlagIcon'
import { useAlertContext } from '@/contexts/AlertContext'
import { useActivationGroupsQuery } from '@/features/activationGroup/hooks/useActivationGroupsQuery'
import RegulationObjectsDataGridToolbar from '@/features/portfolioHierarchy/components/RegulationObjectsDataGridToolbar'
import { PortfolioHierarchyRouteInformation } from '@/features/portfolioHierarchy/constants'
import type { RegulationObject } from '@/features/portfolioHierarchy/utils/resolveRegulationObjects'
import { resolveRegulationObjects } from '@/features/portfolioHierarchy/utils/resolveRegulationObjects'

const RegulationObjectsDataGrid = () => {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { isLoading, isError, activationGroups } = useActivationGroupsQuery()

  useEffect(() => {
    if (isError) {
      pushAlert({
        message: t('portfolio_hierarchy.regulation_object.error_loading'),
        severity: 'error',
      })
    }
  }, [isError, pushAlert, t])

  const columns: GridColDef<RegulationObject>[] = [
    {
      field: 'regulationObjectCode',
      headerName: t('portfolio_hierarchy.regulation_object.code'),
      flex: 5,
    },
    {
      field: 'countryCode',
      headerName: t('portfolio_hierarchy.portfolio.country_code'),
      flex: 3,
      renderCell: (params) => {
        return (
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', height: '100%', whiteSpace: 'nowrap' }}>
            <FlagIcon countryCode={params.row.countryCode?.toLowerCase() as CountryIdentifier} />
            <span style={{ marginLeft: 4 }}>
              {t(`common.country.${params.value.toLowerCase() as CountryIdentifier}`)}
            </span>
          </Stack>
        )
      },
    },
    {
      field: 'priceArea',
      headerName: t('portfolio_hierarchy.portfolio.price_area'),
      flex: 2,
    },
    {
      field: 'portfolioName',
      headerName: t('portfolio_hierarchy.portfolio.name'),
      flex: 5,
    },
    {
      field: 'portfolioCode',
      headerName: t('portfolio_hierarchy.portfolio.code'),
      flex: 5,
    },
    {
      field: 'activationGroupsCount',
      headerName: t('portfolio_hierarchy.regulation_object.activation_groups_count'),
      flex: 3,
      type: 'number',
    },
  ]

  const regulationObjects = useMemo(() => resolveRegulationObjects(activationGroups) ?? [], [activationGroups])

  return (
    <CustomDataGrid
      clickableRows={{
        navigateTo: ({ row }: GridRowParams<RegulationObject>) =>
          PortfolioHierarchyRouteInformation.REGULATION_OBJECT_DETAILS.navigationPath(
            row.countryCode,
            row.regulationObjectCode,
          ),
      }}
      columns={columns}
      getRowId={(row: RegulationObject) => row.key}
      initialState={{
        sorting: {
          sortModel: [
            { field: 'countryCode', sort: 'asc' },
            { field: 'regulationObjectCode', sort: 'asc' },
          ],
        },
      }}
      isLoading={isLoading}
      rows={regulationObjects}
      slotProps={{
        root: {
          'data-testid': 'regulation-objects-grid',
        },
        toolbar: {
          regulationObjects,
        },
      }}
      slots={{ toolbar: RegulationObjectsDataGridToolbar }}
    />
  )
}

export default RegulationObjectsDataGrid
